import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle } from "lucide-react";

const BEFORE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3476f53d-6e93-46c6-acbb-e5a886d983f7/images/21fcc22e28ee833c6a1ee295364d8f57c1d14fcb5bc3dd8b9a1004ad349fa473.png";
const AFTER_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3476f53d-6e93-46c6-acbb-e5a886d983f7/images/5c1328360c8a34a401e887a95277b150e089a2f6603451b6799ddf4225d935cf.png";

const BeforeIssues = [
  "Outdated layout",
  "Hard to scan",
  "Keyword-poor",
  "Not ATS-friendly",
];
const AfterWins = [
  "Modern, clean design",
  "Optimized for recruiters",
  "Keyword-rich for ATS",
  "Professional formatting",
];

export const ResumeExamples = () => {
  return (
    <section
      id="examples"
      className="relative py-24 sm:py-32"
      data-testid="examples-section"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xs uppercase tracking-[0.18em] text-zinc-500"
          >
            Resume Transformations
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance"
            data-testid="examples-headline"
          >
            See the difference an AI-crafted resume makes.
          </motion.h2>
          <p className="mt-4 text-zinc-400 max-w-xl">
            From cluttered to clear. From generic to recruiter-ready. Every CV
            is rebuilt for design, readability, and ATS compatibility.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Before */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl border border-white/10 bg-zinc-950/60 p-5 sm:p-7"
            data-testid="example-before-card"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-red-500/10 border border-red-500/20 px-3 py-1 text-xs text-red-300">
                <XCircle className="h-3.5 w-3.5" /> Before
              </div>
              <span className="text-[11px] text-zinc-500">Old resume</span>
            </div>
            <div className="overflow-hidden rounded-xl border border-white/5 bg-zinc-900">
              <img
                src={BEFORE_IMG}
                alt="Before resume sample"
                className="w-full h-auto object-cover"
                loading="lazy"
                data-testid="example-before-image"
              />
            </div>
            <ul className="mt-5 grid grid-cols-2 gap-2">
              {BeforeIssues.map((i) => (
                <li
                  key={i}
                  className="text-xs text-zinc-400 flex items-center gap-2"
                >
                  <XCircle className="h-3.5 w-3.5 text-red-400/80" />
                  {i}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* After */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-5 sm:p-7 overflow-hidden"
            data-testid="example-after-card"
          >
            <div
              aria-hidden
              className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-purple-600/20 blur-[100px]"
            />
            <div
              aria-hidden
              className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-blue-600/20 blur-[100px]"
            />
            <div className="relative flex items-center justify-between mb-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 text-xs text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" /> After · CV AI PRO
              </div>
              <span className="text-[11px] text-zinc-500">Optimized</span>
            </div>
            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-zinc-900">
              <img
                src={AFTER_IMG}
                alt="After resume sample"
                className="w-full h-auto object-cover"
                loading="lazy"
                data-testid="example-after-image"
              />
            </div>
            <ul className="relative mt-5 grid grid-cols-2 gap-2">
              {AfterWins.map((i) => (
                <li
                  key={i}
                  className="text-xs text-zinc-300 flex items-center gap-2"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  {i}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
